import React, { useState, useEffect, useCallback, useMemo, useRef, Suspense, startTransition } from 'react';
import { bindActionCreators } from 'redux';
import { useHistory, useLocation } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { useQueryClient } from '@tanstack/react-query';
import { ErrorBoundary } from 'react-error-boundary';
import BaselineTaskListDesktop from './BaselineTaskListDesktop';
import BaselineTaskListMobile from './BaselineTaskListMobile';
import BaselineTaskDrawer from './BaselineTaskDrawer';
import FiltersDrawer from './FiltersDrawer';
import { actions as filesActions } from '@Actions/filesActions';
import { actions as taskTagActions } from '@Actions/taskTagActions';
import URLSearchParams from '@ungap/url-search-params';
import { BaselineTaskItem } from '../../types/common';
import {
	MOBILE_WINDOW_BREAKPOINT,
	DEFAULT_PAGE_SIZE,
	BASELINE_TASK_LIST_URL_PARAMS as URLParams,
	TASK_LIST_VIEWS,
	LIST_POLL_INTERVAL_MS,
} from '../../constants/constants';
import Notification from '../Common/Notification';
import LoadingIndicator from '../Common/LoadingIndicator/LoadingIndicator';
import { useLoadBaselineList } from '../../apis/baselines';
import {
	useSearchBaselineTasks,
	useLoadBaselineTaskListFilters,
	useLoadBaselineTaskFormFilters,
	BaselineTaskSearch,
	SEARCH_QUERY_KEY,
	FORM_FILTERS_QUERY_KEY,
} from '../../apis/baselineTasks';
import Routes from '../../constants/routes';
import moment from 'moment';

// Redux state interface (matching original implementation)
interface RootState {
	window: {
		width: number;
	};
	user: {
		currentSubscriptionId: number;
	};
}

// Initial filters placeholder
const initialFilters = {
	isStartDateActive: true,
	isTaskStepsActive: true,
	isTaskEffortActive: true,
	isTaskFeedbackActive: true,
	isTaskDependenciesActive: true,
	isProjectWidgetActive: false,
	departments: [],
	baselineJobTitles: [],
	communities: [],
	phases: [],
	disciplines: [],
	tags: [],
};

// Loading Component for Suspense (following BaselinesPage pattern)
const LoadingComponent: React.FC = () => (
	<LoadingIndicator isLoading={true}>
		<div>Loading baseline task list...</div>
	</LoadingIndicator>
);

// Error Fallback Component (following BaselinesPage pattern)
const ErrorFallback: React.FC<{ error: unknown; resetErrorBoundary: () => void }> = ({
	error,
	resetErrorBoundary
}) => (
	<div className="alert alert-danger m-3">
		<h5>Something went wrong with baseline task list:</h5>
		<p>{error instanceof Error ? error.message : String(error)}</p>
		<button className="btn btn-secondary" onClick={resetErrorBoundary}>
			Try again
		</button>
	</div>
);

const BaselineTaskListContent = () => {
	const dispatch = useDispatch();
	const location = useLocation();
	const history = useHistory();
	const queryClient = useQueryClient();

	// Get values from Redux store (matching original implementation)
	const windowWidth = useSelector((state: RootState) => state.window.width);
	const subscriptionId = useSelector((state: RootState) => state.user.currentSubscriptionId);

	// Parse URL parameters
	const urlParams = useMemo(() => {
		const searchParams = new URLSearchParams(location.search);
		return {
			baselineId: searchParams.get(URLParams.BaselineId) || '',
			baselineTaskId: searchParams.get(URLParams.BaselineTaskId) || '',
		};
	}, [location.search]);

	const isMobile = windowWidth < MOBILE_WINDOW_BREAKPOINT;

	// Individual state variables (following BaselinesPage pattern)
	const [editTaskId, setEditTaskId] = useState<number>(0);
	const [isDrawerOpen, setIsDrawerOpen] = useState<boolean>(false);
	const [showFiltersDrawer, setShowFiltersDrawer] = useState<boolean>(false);
	const [checkedTasks, setCheckedTasks] = useState<BaselineTaskItem[]>([]);
	const [allTasksChecked, setAllTasksChecked] = useState<boolean>(false);
	const [columnOrder, setColumnOrder] = useState<string[]>([]);
	// The saved order only needs to be applied once; later polls must not overwrite in-session changes.
	const hasAppliedSavedColumnOrder = useRef<boolean>(false);
	const [selectedView, setSelectedView] = useState<string>(TASK_LIST_VIEWS.List.value);
	const [lastFilterChange, setLastFilterChange] = useState<Date>(new Date());

	// Separate display state for text inputs (doesn't trigger search)
	const [displayParams, setDisplayParams] = useState(() => ({
		name: '',
		additionalInfo: ''
	}));
	const [searchParams, setSearchParams] = useState<BaselineTaskSearch>(() => ({
		subscriptionId: subscriptionId.toString(),
		baselineId: urlParams.baselineId || '',
		departments: [],
		baselineJobTitles: [],
		communities: [],
		phases: [],
		disciplines: [],
		tags: [],
		isMobile,
		isActive: true,
		pageSize: DEFAULT_PAGE_SIZE,
		pageNumber: 1,
		sortBy: '',
		sortDir: '',
		name: '',
		additionalInfo: '',
		multiColumnSort: [
			{ sortBy: 'targetDaysOut', sortDir: 'desc' },
			{ sortBy: 'targetStaticDate', sortDir: 'asc' },
			{ sortBy: 'ordinal', sortDir: 'asc' },
		],
		createdDateStart: null,
		createdDateEnd: null,
	}));

	const handleEditTask = useCallback((taskId: number) => {
		startTransition(() => {
			setEditTaskId(taskId || 0);
			setIsDrawerOpen(true);
		});
	}, []);

	// Sanitize baseline tasks to ensure all required fields are present and valid for zod (following BaselinesPage pattern)
	const sanitizeBaselineTask = useCallback((task: any): BaselineTaskItem => ({
		...task,
		id: typeof task.id === 'number' ? task.id : 0,
		name: task.name || '',
		isActive: typeof task.isActive === 'boolean' ? task.isActive : true,
		isDeprecated: typeof task.isDeprecated === 'boolean' ? task.isDeprecated : false,
		createdDate: task.createdDate && moment(task.createdDate).isValid()
			? moment(task.createdDate).toDate()
			: new Date(),
		isChecked: typeof task.isChecked === 'boolean' ? task.isChecked : false,
		targetDaysOut: typeof task.targetDaysOut === 'number' ? task.targetDaysOut : 0,
		anticipatedEffort: task.anticipatedEffort || '',
		ordinal: task.ordinal || 0,
		isMilestone: typeof task.isMilestone === 'boolean' ? task.isMilestone : false,
		additionalInfo: task.additionalInfo || '',
		links: Array.isArray(task.links) ? task.links : [],
		contactName: task.contactName || '',
		contactEmail: task.contactEmail || '',
		baselineJobTitle: task.baselineJobTitle || '',
		department: task.department || '',
		community: task.community || '',
		discipline: task.discipline || '',
		tags: Array.isArray(task.tags) ? task.tags : [],
		totalDependencies: typeof task.totalDependencies === 'number' ? task.totalDependencies : 0,
		totalSubtasks: typeof task.totalSubtasks === 'number' ? task.totalSubtasks : 0,
	}), []);

	// Parse multi-select column filters (from original JS implementation)
	const parseMultiSelectColumnFilters = useCallback(() => {
		const fields = ['departments', 'baselineJobTitles', 'communities', 'phases', 'disciplines', 'tags'];
		const result: any = {};

		// If the field contains an id, we return that id. Otherwise we just return
		// the array as it is
		fields.forEach((field) => {
			const values = searchParams[field as keyof typeof searchParams] || [];
			result[field] = Array.isArray(values) ? values.map((x: any) => (x && typeof x === 'object' ? x.id : x)) : [];
		});

		return result;
	}, [searchParams]);

	// Queries with error handling (following BaselinesPage pattern)
	const { data: baselinesList, isFetching: isFetchingBaselines, isLoading: isLoadingBaselines, error: baselinesError } = useLoadBaselineList(
		subscriptionId,
		{ enabled: !!subscriptionId && subscriptionId > 0 }
	);

	const { data: searchResults, isFetching: isFetchingTasks, isLoading: isLoadingTasks, error: searchError } = useSearchBaselineTasks(
		{
			...searchParams,
			...parseMultiSelectColumnFilters()
		},
		{
			enabled: !!searchParams.baselineId && searchParams.baselineId !== '' && !!subscriptionId && subscriptionId > 0,
			refetchInterval: LIST_POLL_INTERVAL_MS
		}
	);

	const { data: listFiltersData, isFetching: isFetchingFilters, isLoading: isLoadingFilters, error: listFiltersError } = useLoadBaselineTaskListFilters(
		{ subscriptionId: subscriptionId.toString(), baselineId: searchParams.baselineId.toString() },
		{
			enabled: !!searchParams.baselineId && searchParams.baselineId !== '' && !!subscriptionId && subscriptionId > 0
		}
	);

	const { data: formFiltersData, error: formFiltersError } = useLoadBaselineTaskFormFilters(
		{ subscriptionId: subscriptionId.toString(), baselineId: searchParams.baselineId },
		{ enabled: !!searchParams.baselineId && isDrawerOpen && !!subscriptionId && subscriptionId > 0 }
	);

	// Handle query errors (following BaselinesPage pattern)
	useEffect(() => {
		if (baselinesError) {
			Notification('error', 'Error Loading Baselines', baselinesError.message);
		}
	}, [baselinesError]);

	useEffect(() => {
		if (searchError) {
			Notification('error', 'Error Loading Tasks', searchError.message);
		}
	}, [searchError]);

	useEffect(() => {
		if (listFiltersError) {
			Notification('error', 'Error Loading Filters', listFiltersError.message);
		}
	}, [listFiltersError]);

	useEffect(() => {
		if (formFiltersError) {
			Notification('error', 'Error Loading Form Filters', formFiltersError.message);
		}
	}, [formFiltersError]);

	// Loading state (following BaselinesPage pattern)
	// Every flag here must be isLoading, not isFetching: this feeds a hideWhileLoading indicator, and
	// the QueryClient has no staleTime and leaves refetchOnWindowFocus on, so an isFetching flag would
	// unmount the whole page on every tab refocus - closing open drawers and resetting local state.
	// The 30s task poll would do the same.
	const isLoading = isLoadingBaselines || isLoadingTasks || isLoadingFilters;

	const clearAllCheckedTasks = useCallback(() => {
		startTransition(() => {
			setCheckedTasks([]);
			setAllTasksChecked(false);
		});
	}, []);

	// Computed values (following BaselinesPage pattern)
	const baselineTasks = useMemo(() => {
		const tasks = searchResults?.results?.map(sanitizeBaselineTask) || [];
		// Add IsChecked property for each task
		return tasks.map(task => ({
			...task,
			IsChecked: checkedTasks.some(checkedTask => checkedTask.id === task.id)
		}));
	}, [searchResults?.results, sanitizeBaselineTask, checkedTasks]);

	const totalCount = searchResults?.totalCount || 0;

	// Restore the user's saved column selection/order returned by the search endpoint.
	useEffect(() => {
		if (hasAppliedSavedColumnOrder.current) return;

		const savedColumnOrder = searchResults?.columnOrder;
		if (!savedColumnOrder || savedColumnOrder.length === 0) return;

		hasAppliedSavedColumnOrder.current = true;
		setColumnOrder(savedColumnOrder);
	}, [searchResults?.columnOrder]);

	// Sync display params with search params when search params change externally
	useEffect(() => {
		setDisplayParams({
			name: searchParams.name || '',
			additionalInfo: searchParams.additionalInfo || ''
		});
	}, [searchParams.name, searchParams.additionalInfo]);

	// Handle URL baseline task ID (from original JS implementation)
	useEffect(() => {
		if (urlParams.baselineTaskId && baselineTasks.length > 0) {
			const baselineTaskId = parseInt(urlParams.baselineTaskId);
			handleEditTask(baselineTaskId);
		}
	}, [urlParams.baselineTaskId, baselineTasks.length, handleEditTask]);

	// Event handlers with transition wrapping (following BaselinesPage pattern)
	const handleAddTask = useCallback(() => {
		startTransition(() => {
			setEditTaskId(0);
			setIsDrawerOpen(true);
		});
	}, []);

	const handleCloseDrawer = useCallback(() => {
		startTransition(() => {
			setIsDrawerOpen(false);
			setEditTaskId(0);
		});
	}, []);

	// Refresh the task list without touching the drawer. Used by Save and New, which saves the
	// task but keeps the drawer open on a cleared form for the next entry.
	const handleListRefresh = useCallback(() => {
		startTransition(() => {
			setLastFilterChange(new Date());
			// Clear checked tasks and refresh data
			clearAllCheckedTasks();
			// Invalidate queries to refresh data
			queryClient.invalidateQueries({ queryKey: [SEARCH_QUERY_KEY] });
			queryClient.invalidateQueries({ queryKey: [FORM_FILTERS_QUERY_KEY] });
		});
	}, [queryClient, clearAllCheckedTasks]);

	const handleRefreshAfterSave = useCallback(() => {
		// Trigger a refresh of the task list after saving (like onDrawerComplete in JS version)
		startTransition(() => {
			setIsDrawerOpen(false);
			setEditTaskId(0);
		});
		handleListRefresh();
	}, [handleListRefresh]);

	const handleBaselineChange = useCallback((baselineId: string | number) => {
		startTransition(() => {
			setSearchParams(prev => ({
				...prev,
				baselineId: baselineId.toString(),
				pageNumber: 1,
				// Reset filters when changing baseline
				name: '',
				additionalInfo: '',
				departments: [],
				baselineJobTitles: [],
				communities: [],
				phases: [],
				disciplines: [],
				tags: []
			}));
			// Update URL like in original implementation
			history.push(Routes.BaselineTaskList + '/?bid=' + baselineId);
		});
	}, [history]);

	const handleSortChange = useCallback((sortBy: string, sortDir: string) => {
		// Only search again if the values have changed (matching JS version logic)
		if (searchParams.sortBy !== sortBy || searchParams.sortDir !== sortDir) {
			// Once a column has been clicked, the default multi-column sorting is no longer valid, so clear it out.
			startTransition(() => {
				setSearchParams(prev => ({
					...prev,
					sortBy,
					sortDir,
					multiColumnSort: [],
					pageNumber: 1
				}));
			});
		}
	}, [searchParams.sortBy, searchParams.sortDir]);

	// Task checking functionality (from original JS implementation)
	const onToggleCheckedTask = useCallback((baselineTaskId: number) => {
		const tasks = baselineTasks;
		let newCheckedTasks: BaselineTaskItem[] = [];
		const newTasks = tasks.map((item) => {
			let task = { ...item };
			if (item.id === baselineTaskId) {
				task.IsChecked = !item.IsChecked;
			}

			if (task.IsChecked) {
				newCheckedTasks.push(task);
			}
			return task;
		});

		// If we've manually checked all tasks, also check the master checkbox
		const allTasksChecked = newTasks.length === newCheckedTasks.length;

		startTransition(() => {
			setCheckedTasks(newCheckedTasks);
			setAllTasksChecked(allTasksChecked);
		});
	}, [baselineTasks]);

	const onMasterCheckboxToggle = useCallback(() => {
		// Toggle the master checkbox to checked or unchecked
		const newAllTasksChecked = !allTasksChecked;

		// Check or uncheck all baseline tasks, based on its newly-toggled state
		const newCheckedTasks = newAllTasksChecked ? baselineTasks.slice() : [];

		startTransition(() => {
			setCheckedTasks(newCheckedTasks);
			setAllTasksChecked(newAllTasksChecked);
		});
	}, [allTasksChecked, baselineTasks]);


	// Filters drawer functionality (from original JS implementation)
	const onViewFiltersClick = useCallback(() => {
		startTransition(() => {
			setShowFiltersDrawer(true);
		});
	}, []);

	const toIds = (arr: any[]): number[] =>
		(arr || []).map((x: any) => (x && typeof x === 'object' ? Number(x.id) : Number(x))).filter(Boolean);

	const onViewFiltersComplete = useCallback((newParams: BaselineTaskSearch) => {
		// FiltersDrawer stores multi-select fields as {id} objects; normalise to numeric IDs
		// so getColumnFilterMultiSelectValues (which expects number[]) works correctly.

		startTransition(() => {
			setShowFiltersDrawer(false);
			setLastFilterChange(new Date());
			setSearchParams({
				...newParams,
				pageNumber: 1,
				departments: toIds(newParams.departments),
				baselineJobTitles: toIds(newParams.baselineJobTitles),
				communities: toIds(newParams.communities),
				phases: toIds(newParams.phases),
				disciplines: toIds(newParams.disciplines),
				tags: toIds(newParams.tags),
			});
		});
	}, []);

	const onViewFiltersCancel = useCallback(() => {
		startTransition(() => {
			setShowFiltersDrawer(false);
		});
	}, []);

	const onViewSelectorChange = useCallback((viewName: string) => {
		startTransition(() => {
			setLastFilterChange(new Date());
			setSearchParams(prev => ({
				...prev,
				pageSize: DEFAULT_PAGE_SIZE,
				pageNumber: 1,
			}));
			setSelectedView(viewName);
		});
	}, []);

	const onColumnOrderChange = useCallback((order: string[]) => {
		hasAppliedSavedColumnOrder.current = true;
		startTransition(() => {
			setColumnOrder(order);
		});
	}, []);

	const mergedFormFilters = useMemo(() => ({
		...initialFilters,
		...listFiltersData,
		...formFiltersData,
	}), [listFiltersData, formFiltersData]);

	// Bind action creators to dispatch so thunks are dispatched correctly
	const boundFilesActions = useMemo(() => bindActionCreators(filesActions, dispatch), [dispatch]);
	const boundTaskTagActions = useMemo(() => bindActionCreators(taskTagActions, dispatch), [dispatch]);

	// Common props for child components (adapting to legacy interface)
	const commonProps = {
		// Required props for legacy components
		subscriptionId: subscriptionId.toString(),
		baselines: (baselinesList?.baselines || []).map(baseline => ({
			id: baseline.id,
			name: baseline.name,
		})),
		baselineTasks,
		searchParams: {
			...searchParams,
			sortBy: searchParams.sortBy || '',
			sortDir: searchParams.sortDir || '',
			departments: searchParams.departments || [],
			baselineJobTitles: searchParams.baselineJobTitles || [],
			communities: searchParams.communities || [],
			phases: searchParams.phases || [],
			disciplines: searchParams.disciplines || [],
			tags: searchParams.tags || [],
			// Use display params for text fields to show current typing
			name: displayParams.name,
			additionalInfo: displayParams.additionalInfo
		},
		totalCount,
		isFetchingBaselines,
		isFetchingTasks,
		isFetchingFilters,
		// The isLoading* flags are true only on the first, uncached load. Consumers that unmount
		// their content while loading (hideWhileLoading, or a spinner in place of the children)
		// must use these so neither the 30s poll nor a window refocus blanks them.
		isLoadingBaselines,
		isLoadingTasks,
		isLoadingFilters,
		editTaskId,
		isDrawerOpen,

		// Filter options
		departments: listFiltersData?.departments || [],
		baselineJobTitles: listFiltersData?.baselineJobTitles || [],
		communities: listFiltersData?.communities || [],
		phases: listFiltersData?.phases || [],
		disciplines: listFiltersData?.disciplines || [],
		tags: listFiltersData?.tags || [],

		// Event handlers
		clearAllCheckedTasks,
		updateSearchParams: useCallback((name: string, value: any) => {
			// For text inputs, only update display state without triggering search
			if (name === 'name' || name === 'additionalInfo') {
				setDisplayParams(prev => ({ ...prev, [name]: value }));
			} else {
				// For other inputs (dropdowns), update search params and trigger search immediately.
				// A new filter shrinks the result set, so go back to the first page.
				startTransition(() => {
					setLastFilterChange(new Date());
					// pageNumber first so a caller passing 'pageNumber' is not silently overwritten.
					setSearchParams(prev => ({ ...prev, pageNumber: 1, [name]: value }));
				});
			}
		}, []),

		updateParamsAndSearch: useCallback(async (name?: string, value?: any) => {
			if (name === 'baselineId') {
				// Special handling for baseline changes
				handleBaselineChange(value);
				// Note: loadListFilters and clearFilters will be handled by React Query automatically
			} else if (name) {
				// Update params and trigger search (used for dropdowns, pagination, etc.)
				// Filter changes reset back to the first page, but paging and post-action
				// refreshes must keep the page the user is on.
				const keepsCurrentPage = name === 'pageNumber' || name === 'refreshKey';
				startTransition(() => {
					setLastFilterChange(new Date());
					setSearchParams(prev => ({
						...prev,
						[name]: value,
						...(keepsCurrentPage ? {} : { pageNumber: 1 })
					}));
				});
			} else {
				// Trigger search with current display params - this is used for text input blur/enter events
				startTransition(() => {
					setLastFilterChange(new Date());
					// Update search params with current display params
					setSearchParams(prev => ({
						...prev,
						name: displayParams.name,
						additionalInfo: displayParams.additionalInfo,
						pageNumber: 1
					}));
				});
			}
		}, [handleBaselineChange, displayParams]),

		showTask: useCallback((taskId?: number) => {
			if (taskId) {
				handleEditTask(taskId);
			} else {
				handleAddTask();
			}
		}, [handleEditTask, handleAddTask]),
		onSort: handleSortChange,

		// Handlers for legacy interface  
		onToggleCheckedTask,
		onMasterCheckboxToggle,
		allTasksChecked,
		checkedTasks,
		onBulkPushdownComplete: useCallback(() => {
			// Refresh data after bulk operations
			startTransition(() => {
				setLastFilterChange(new Date());
				setSearchParams(prev => ({ ...prev }));
			});
		}, []),
		onComplete: useCallback(() => {
			// Refresh data
			startTransition(() => {
				setLastFilterChange(new Date());
				setSearchParams(prev => ({ ...prev }));
			});
		}, []),
		hasNoFilters: useCallback(() => {
			// Check if any filters are applied
			return (
				!searchParams.name &&
				!searchParams.additionalInfo &&
				searchParams.departments.length === 0 &&
				searchParams.baselineJobTitles.length === 0 &&
				searchParams.communities.length === 0 &&
				searchParams.phases.length === 0 &&
				searchParams.disciplines.length === 0 &&
				searchParams.tags.length === 0 &&
				searchParams.isActive === true &&
				!searchParams.createdDateStart &&
				!searchParams.createdDateEnd
			);
		}, [searchParams]),
		clearFilters: useCallback(() => {
			startTransition(() => {
				// Clear both search params and display params
				setDisplayParams({ name: '', additionalInfo: '' });
				setSearchParams(prev => ({
					...prev,
					name: '',
					additionalInfo: '',
					departments: [],
					baselineJobTitles: [],
					communities: [],
					phases: [],
					disciplines: [],
					tags: [],
					isActive: true,
					createdDateStart: null,
					createdDateEnd: null,
					pageNumber: 1,
				}));
			});
		}, []),
		updateCreatedDateParams: useCallback((start: Date | null, end: Date | null) => {
			startTransition(() => {
				setSearchParams(prev => ({
					...prev,
					createdDateStart: start ? start.toISOString() : null,
					createdDateEnd: end ? end.toISOString() : null,
					pageNumber: 1
				}));
			});
		}, []),
		onViewSelectorChange,
		onViewFiltersClick,
		onViewFiltersComplete,
		onViewFiltersCancel,
		lastFilterChange,
		selectedView,
		columnOrder,
		onColumnOrderChange,
		formFilters: mergedFormFilters,

		// Action creators bound to dispatch so thunks work correctly
		filesActions: boundFilesActions,
		taskTagActions: boundTaskTagActions,
	} as any; // Type assertion to bypass complex legacy interface

	return (
		<div className="baseline-task-list-page">
			<LoadingIndicator isLoading={isLoading} hideWhileLoading={true}>
				<div>
					{isMobile ? (
						<BaselineTaskListMobile {...commonProps} />
					) : (
						<BaselineTaskListDesktop {...commonProps} />
					)}

					{isDrawerOpen && (
						<BaselineTaskDrawer
							subscriptionId={subscriptionId.toString()}
							baselineId={parseInt(searchParams.baselineId) || 0}
							taskId={editTaskId}
							onComplete={handleRefreshAfterSave}
							onCancel={handleCloseDrawer}
							onUpdateList={handleListRefresh}
							formFilters={mergedFormFilters}
							isMobile={isMobile}
						/>
					)}

					{showFiltersDrawer && (
						<FiltersDrawer
							onComplete={onViewFiltersComplete}
							onCancel={onViewFiltersCancel}
							searchParams={searchParams}
							departments={listFiltersData?.departments || []}
							baselineJobTitles={listFiltersData?.baselineJobTitles || []}
							communities={listFiltersData?.communities || []}
							phases={listFiltersData?.phases || []}
							disciplines={listFiltersData?.disciplines || []}
						/>
					)}
				</div>
			</LoadingIndicator>
		</div>
	);
};

// Main page component with ErrorBoundary and Suspense
const BaselineTaskListController: React.FC = () => {
	return (
		<ErrorBoundary fallbackRender={ErrorFallback}>
			<Suspense fallback={<LoadingComponent />}>
				<BaselineTaskListContent />
			</Suspense>
		</ErrorBoundary>
	);
};

export default BaselineTaskListController;